/**
 * Browser-owned occurrence planning. No store writes, network, random IDs or
 * live-frame reads: a caller captures those inputs and guards them at commit.
 */
import { canonicalizeAssetBackedMediaHTML } from '../shared/asset-html.js';
import { commentAnchorForElement, parseCommentAnchor } from '../shared/comment-anchor.js';
import { applyPreviewElementAttributeOverrides } from '../shared/element-attribute-overrides.js';
import { canonicalStringify } from '../shared/collab-protocol.js';
import { sha256Hex } from '../shared/site-deploy.js';
import * as csstree from '../vendor/csstree.esm.js';
import {
  verifyEditableIdRepairs,
  createEditableIdRepairRecord,
  hashEditableIdRepairFields,
  commentAnchorFingerprint,
  projectCommentAnchor,
  reservedEditableIds
} from '../shared/editable-id-repair.js';

const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const same = (a, b) => canonicalStringify(a) === canonicalStringify(b);
const IMAGE_FIELDS = ['imageTx', 'imageTy', 'imageScale', 'imageHover', 'imageEffect'];
const IMAGE_VARIABLES = {
  imageTx: '--pb-image-tx-pct',
  imageTy: '--pb-image-ty-pct',
  imageScale: '--pb-image-scale'
};
const COMPANIONS = ['html', 'originalHtml', 'overrides', 'editableIdRepairs'];
const parse = (html) =>
  new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html').body.firstElementChild;
const nodes = (root) => Array.from(root.querySelectorAll('[data-pb-eid]'));

function signature(anchor) {
  return {
    tag: anchor.tag,
    attrs: anchor.attrs,
    classes: [...anchor.classes].sort(),
    textDigest: anchor.textDigest
  };
}

function childPath(root, element) {
  const path = [];
  for (let node = element; node && node !== root; node = node.parentElement) {
    path.unshift({
      tag: node.tagName.toLowerCase(),
      index: Array.from(node.parentElement.children).indexOf(node)
    });
  }
  return path;
}

function groupsOf(root) {
  const groups = new Map();
  for (const node of nodes(root)) {
    const id = node.getAttribute('data-pb-eid');
    if (id) groups.set(id, [...(groups.get(id) || []), node]);
  }
  return groups;
}

function sectionEntries(state) {
  const entries = [];
  for (const key of ['navigation', 'footer']) {
    const section = state?.sharedSections?.[key];
    if (section) entries.push({ section, sectionPath: `sharedSections.${key}`, pageId: '*' });
  }
  for (const [pi, page] of (state?.pages || []).entries()) {
    for (const [si, section] of (page.sections || []).entries()) {
      entries.push({ section, sectionPath: `pages.${pi}.sections.${si}`, pageId: page.id });
    }
  }
  return entries;
}

/**
 * All persistent eid-keyed stores: overrides.elements and every
 * overrides.responsive[viewport].elements. HTML/reset/provenance are companion
 * fields; server comment anchors are projected, never rewritten. Selection,
 * iframe hit/rect caches and inspector signatures are invalidated at commit.
 * CSS/entrance selectors already affect every duplicate. Attribute assembly
 * acts on the first match only; later inactive image fields and SVG aspect
 * overrides must therefore be omitted from their base copies.
 */
export function remapEidsInSection(section, plan) {
  const after = structuredClone(section);
  const buckets = [after.overrides, ...Object.values(after.overrides?.responsive || {})];
  for (const bucket of buckets) {
    if (!bucket?.elements) continue;
    const entries = [];
    for (const [eid, props] of Object.entries(bucket.elements)) {
      entries.push([eid, props]);
      for (const occurrence of plan.filter((item) => item.fromEid === eid && item.toEid !== eid)) {
        const copy = structuredClone(props);
        if (bucket === after.overrides && copy && typeof copy === 'object') {
          for (const field of occurrence.omittedFirstMatchFields) delete copy[field];
        }
        entries.push([occurrence.toEid, copy]);
      }
    }
    bucket.elements = Object.fromEntries(entries);
  }
  return after;
}

function companionPatches(path, section) {
  return COMPANIONS.map((field) =>
    own(section, field) && section[field] !== undefined
      ? { op: 'replace', path: `${path}.${field}`, value: structuredClone(section[field]) }
      : { op: 'replace', path: `${path}.${field}`, value: null, absent: true }
  );
}

async function planSection(
  entry,
  { projectId, threads, runtimeMayAffectImages, authoredImageVariables }
) {
  const { section } = entry;
  const canonical = {
    ...section,
    html: canonicalizeAssetBackedMediaHTML(section.html, { parseHTML: parse })
  };
  const live = parse(canonical.html);
  const groups = groupsOf(live);
  const duplicateGroups = [...groups].filter(([, group]) => group.length > 1);
  if (!duplicateGroups.length) return null;
  if (!section.originalHtml) throw new Error('missing original baseline');
  canonical.originalHtml = canonicalizeAssetBackedMediaHTML(section.originalHtml, {
    parseHTML: parse
  });
  const original = parse(canonical.originalHtml);
  const originals = groupsOf(original);
  const verified = await verifyEditableIdRepairs(section.editableIdRepairs, {
    instanceId: section.instanceId
  });
  if (!verified.ok) throw new Error(verified.reason);
  const reserved = reservedEditableIds(section.editableIdRepairs);
  for (const id of [...groups.keys(), ...originals.keys()]) reserved.add(id);
  for (const bucket of [section.overrides, ...Object.values(section.overrides?.responsive || {})]) {
    for (const id of Object.keys(bucket?.elements || {})) reserved.add(id);
  }
  let ceiling = Math.max(
    0,
    ...[...reserved].filter((id) => /^e\d+$/.test(id)).map((id) => Number(id.slice(1)))
  );
  if (!Number.isSafeInteger(ceiling)) throw new Error('editable ID ceiling is unsupported');
  const assembled = parse(applyPreviewElementAttributeOverrides(canonical.html, canonical));
  const assembledNodes = nodes(assembled);
  const liveNodes = nodes(live);
  const occurrences = [];
  const pairedNodes = [];
  for (const [eid, group] of duplicateGroups) {
    const originalGroup = originals.get(eid) || [];
    if (originalGroup.length !== group.length) throw new Error('original occurrence count differs');
    const seenSignatures = new Set();
    for (const [index, node] of group.entries()) {
      const anchor = commentAnchorForElement(live, node);
      if (!anchor) throw new Error('ambiguous or unsupported occurrence signature');
      const sig = signature(anchor);
      if (!sig.textDigest && !Object.keys(sig.attrs).length) {
        throw new Error('occurrence signature has no authored content identity');
      }
      const key = canonicalStringify(sig);
      if (seenSignatures.has(key)) throw new Error('ambiguous original occurrence signature');
      seenSignatures.add(key);
      const matching = originalGroup.filter((candidate) => {
        const other = commentAnchorForElement(original, candidate);
        return other && same(signature(other), sig);
      });
      if (matching.length !== 1) throw new Error('original occurrence cannot be uniquely matched');
      const livePath = childPath(live, node);
      const originalPath = childPath(original, matching[0]);
      if (!same(livePath, originalPath)) throw new Error('original occurrence moved');
      let toEid = eid;
      if (index > 0) {
        do {
          ceiling += 1;
          toEid = `e${ceiling}`;
        } while (reserved.has(toEid));
        if (!Number.isSafeInteger(ceiling)) throw new Error('editable ID ceiling is unsupported');
        reserved.add(toEid);
      }
      const omittedFirstMatchFields = [];
      const props = section.overrides?.elements?.[eid];
      if (index > 0 && props && sig.tag === 'svg' && own(props, 'preserveAspectRatio')) {
        omittedFirstMatchFields.push('preserveAspectRatio');
      }
      if (index > 0 && sig.tag === 'img' && IMAGE_FIELDS.some((field) => own(props || {}, field))) {
        const dependentVariable = Object.entries(IMAGE_VARIABLES).find(
          ([field, variable]) => own(props, field) && authoredImageVariables.has(variable)
        );
        if (dependentVariable)
          throw new Error(
            `authored CSS reads ${dependentVariable[1]}; a measured supported image migration is required`
          );
        if (authoredImageVariables.has('*'))
          throw new Error('authored CSS image-variable usage could not be parsed');
        const effective = assembledNodes[liveNodes.indexOf(node)];
        if (runtimeMayAffectImages || section.js?.trim()) {
          throw new Error(
            'image runtime may restore engagement markers; measured supported migration is required'
          );
        }
        if (
          !effective ||
          ['data-image-edit', 'data-image-hover', 'data-image-effect'].some((attr) =>
            effective.hasAttribute(attr)
          )
        ) {
          throw new Error('image engagement differs from supported attribute assembly');
        }
        omittedFirstMatchFields.push(...IMAGE_FIELDS.filter((field) => own(props, field)));
      }
      occurrences.push({
        fromEid: eid,
        toEid,
        livePath,
        originalPath,
        commentPath: anchor.path,
        signature: sig,
        omittedFirstMatchFields
      });
      pairedNodes.push({ node, original: matching[0], toEid });
    }
  }
  // The durable record has global DOM order, even when duplicate groups interleave.
  occurrences.sort((a, b) => {
    for (let i = 0; i < Math.min(a.livePath.length, b.livePath.length); i += 1) {
      const delta = a.livePath[i].index - b.livePath[i].index;
      if (delta) return delta;
    }
    return a.livePath.length - b.livePath.length;
  });
  const beforeHtmlHash = await sha256Hex(canonical.html);
  const beforeOriginalHtmlHash = await sha256Hex(canonical.originalHtml);
  const commentBindings = [];
  for (const thread of threads.filter((thread) => thread.instanceId === section.instanceId)) {
    if (!thread.anchorJson) continue;
    const parsed = parseCommentAnchor(thread.anchorJson);
    if (!parsed?.eid) throw new Error('comment anchor is malformed or unsupported');
    const fingerprint = await commentAnchorFingerprint(thread.anchorJson);
    let anchor = parsed;
    if (verified.value?.records.length) {
      const projection = projectCommentAnchor({
        section,
        projectId,
        thread,
        anchorFingerprint: fingerprint,
        currentOriginalHtmlHash: beforeOriginalHtmlHash,
        eidCounts: new Map([...groups].map(([id, group]) => [id, group.length]))
      });
      if (projection.kind === 'unresolved') throw new Error(`comment anchor: ${projection.reason}`);
      anchor = projection.anchor;
    }
    const affected = occurrences.filter((occurrence) => occurrence.fromEid === anchor?.eid);
    if (!affected.length) continue;
    const matches = affected.filter(
      (occurrence) =>
        same(occurrence.commentPath, anchor.path) && same(occurrence.signature, signature(anchor))
    );
    if (matches.length !== 1)
      throw new Error('comment anchor cannot identify one duplicate occurrence');
    commentBindings.push({
      threadId: thread.id,
      identityRevision: parsed.identityRevision ?? null,
      anchorFingerprint: fingerprint,
      fromEid: matches[0].fromEid,
      toEid: matches[0].toEid
    });
  }
  for (const pair of pairedNodes) {
    pair.node.setAttribute('data-pb-eid', pair.toEid);
    pair.original.setAttribute('data-pb-eid', pair.toEid);
  }
  const after = remapEidsInSection(canonical, occurrences);
  after.html = live.innerHTML;
  after.originalHtml = original.innerHTML;
  const record = await createEditableIdRepairRecord({
    projectId,
    instanceId: section.instanceId,
    beforeHtmlHash,
    beforeOriginalHtmlHash,
    afterHtmlHash: await sha256Hex(after.html),
    afterOriginalHtmlHash: await sha256Hex(after.originalHtml),
    previousRecordId: verified.value?.records.at(-1)?.id ?? null,
    occurrences,
    commentBindings,
    transition: {
      before: await hashEditableIdRepairFields(canonical),
      after: await hashEditableIdRepairFields(after)
    }
  });
  after.editableIdRepairs = { version: 1, records: [...(verified.value?.records || []), record] };
  const check = await verifyEditableIdRepairs(after.editableIdRepairs, {
    instanceId: section.instanceId
  });
  if (!check.ok) throw new Error(check.reason);
  return {
    ...entry,
    before: structuredClone(section),
    after,
    replacedCount: occurrences.filter((occurrence) => occurrence.fromEid !== occurrence.toEid)
      .length
  };
}

/** Repair only intra-section duplicates; repeated IDs in separate sections are valid. */
export async function dedupeEditableIdsAcrossState(state, options = {}) {
  const result = { sections: [], refusals: [], forward: [], reverse: [], replacedCount: 0 };
  if (!state || typeof DOMParser === 'undefined') return result;
  const projectId = options.projectId ?? state.projectId ?? null;
  const entries = sectionEntries(state);
  const authoredImageVariables = imageVariableDependencies(entries, options.templateCSS);
  for (const entry of entries) {
    if (!entry.section?.html) continue;
    try {
      const planned = await planSection(entry, {
        projectId,
        threads: options.threads || [],
        runtimeMayAffectImages: options.runtimeMayAffectImages === true,
        authoredImageVariables
      });
      if (!planned) continue;
      result.sections.push(planned);
      result.replacedCount += planned.replacedCount;
      result.forward.push(...companionPatches(entry.sectionPath, planned.after));
      result.reverse.unshift(...companionPatches(entry.sectionPath, planned.before));
    } catch (error) {
      result.refusals.push({
        instanceId: entry.section.instanceId,
        sectionPath: entry.sectionPath,
        message: "Needs manual review: duplicate IDs whose original can't be matched",
        reason: error.message
      });
    }
  }
  return result;
}

/** Examine authored CSS, including indirect var() chains, before dropping fields. */
function imageVariableDependencies(entries, templateCSS = '') {
  const inputs = [{ css: templateCSS, context: 'stylesheet' }];
  for (const { section } of entries) {
    inputs.push({ css: section?.css, context: 'stylesheet' });
    for (const bucket of [
      section?.overrides,
      ...Object.values(section?.overrides?.responsive || {})
    ]) {
      inputs.push({ css: bucket?.customCSS, context: 'stylesheet' });
    }
    if (section?.html?.includes('--pb-image-')) {
      const root = parse(section.html);
      for (const style of root.querySelectorAll('style'))
        inputs.push({ css: style.textContent, context: 'stylesheet' });
      for (const element of root.querySelectorAll('[style]'))
        inputs.push({ css: element.getAttribute('style'), context: 'declarationList' });
    }
  }
  const dependencies = new Set();
  for (const { css, context } of inputs) {
    if (typeof css !== 'string' || !css.includes('--pb-image-')) continue;
    try {
      const ast = csstree.parse(css, {
        context,
        parseCustomProperty: true,
        onParseError: () => dependencies.add('*')
      });
      csstree.walk(ast, (node) => {
        if (node.type !== 'Function' || node.name.toLowerCase() !== 'var') return;
        const variable = node.children.first;
        if (
          variable?.type === 'Identifier' &&
          Object.values(IMAGE_VARIABLES).includes(variable.name)
        )
          dependencies.add(variable.name);
      });
    } catch {
      dependencies.add('*');
    }
  }
  return dependencies;
}
